import React, { useState } from 'react';
import { useClients } from '../hooks/useClients';
import { useAgents } from '../hooks/useAgents';
import { useCalendar } from '../hooks/useCalendar';
import { usePosts } from '../hooks/usePosts';
import { usePermissions } from '../hooks/usePermissions';
import ClientCard from '../components/ClientCard';
import CalendarWidget from '../components/CalendarWidget';
import AgentCard from '../components/AgentCard';
import Modal from '../components/shared/Modal';
import ClientForm from '../components/forms/ClientForm';

function Overview() {
  const { clients, loading, error, createClient, refetch } = useClients();
  const { agents } = useAgents();
  const { upcomingPosts, allPosts } = useCalendar();
  const { updatePostStatus } = usePosts({ onUpdate: refetch });
  const { isAdmin } = usePermissions();
  const [showNewClient, setShowNewClient] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const handleCreateClient = async (data) => {
    setSaving(true);
    try {
      await createClient(data);
      setShowNewClient(false);
      setToast({ type: 'success', message: `Cliente "${data.name}" criado com sucesso` });
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      setToast({ type: 'error', message: `Erro: ${err.message}` });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner" />
        Carregando dashboard...
      </div>
    );
  }

  if (error) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">&#x26A0;</div>
        <p>Erro ao carregar dados: {error}</p>
        <button className="btn btn-primary" style={{ marginTop: 'var(--spacing-md)' }} onClick={refetch}>
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1 className="page-title">SOEVAN Agency</h1>
            <p className="page-subtitle">
              {clients.length} cliente{clients.length !== 1 ? 's' : ''} &middot; {agents.length} agentes &middot; {allPosts.length} posts agendados
            </p>
          </div>
          {isAdmin && (
            <button className="btn btn-primary" onClick={() => setShowNewClient(true)}>
              + Novo Cliente
            </button>
          )}
        </div>
      </div>

      {/* Clientes */}
      <div className="section">
        <h2 className="section-title">Clientes</h2>
        <div className="grid grid-3">
          {clients.map(client => (
            <ClientCard key={client.id} client={client} />
          ))}
        </div>
      </div>

      {/* Proximos Posts */}
      <div className="section">
        <h2 className="section-title">Proximos Posts</h2>
        <div className="card">
          <CalendarWidget posts={upcomingPosts} showClient compact onChangeStatus={updatePostStatus} />
        </div>
      </div>

      {/* Atividade Recente — Agentes */}
      <div className="section">
        <h2 className="section-title">Equipe ({agents.length} agentes)</h2>
        <div className="grid grid-4">
          {agents.slice(0, 8).map(agent => (
            <AgentCard key={agent.id} agent={agent} showSquad />
          ))}
        </div>
      </div>

      {/* Modal novo cliente */}
      {showNewClient && (
        <Modal title="Novo Cliente" onClose={() => setShowNewClient(false)}>
          <ClientForm
            onSubmit={handleCreateClient}
            onCancel={() => setShowNewClient(false)}
            saving={saving}
          />
        </Modal>
      )}

      {/* Toast */}
      {toast && (
        <div className="toast-container">
          <div className={`toast toast--${toast.type}`}>{toast.message}</div>
        </div>
      )}
    </div>
  );
}

export default Overview;
